// frontend-react/src/hooks/useFeatureFlags.ts
import { useAuth } from '../contexts/AuthContext';
import { DEFAULT_COMPANY_FEATURES, CompanyFeatures } from '../services/authService';

/**
 * 統一的功能開關 hook。
 *
 * 讀取 user.company_features（從 /auth/me 取得），並套用預設值，
 * 提供語意清晰的 boolean 旗標給各元件使用。
 */
export const useFeatureFlags = () => {
  const { user } = useAuth();

  const features: CompanyFeatures = {
    ...DEFAULT_COMPANY_FEATURES,
    ...(user?.company_features ?? {}),
  };

  const isAdmin =
    user?.is_superadmin || user?.roles?.includes('ADMIN') || user?.roles?.includes('SUPER_ADMIN');

  // 驗測模式：per-user；Admin/Super Admin 預設擁有，其他使用者需 can_use_validation_mode 授權
  const canToggleValidationMode = !!(isAdmin || user?.permissions?.can_use_validation_mode);
  const validationMode = !!(canToggleValidationMode && user?.validation_mode);

  // 全文審閱：公司層級總開關 + (Admin 或 can_use_ad_review !== false)
  // can_use_ad_review 預設 true（後端 default=True），舊資料 undefined 也視為允許
  const adReviewEnabled = features.ad_review_enabled;
  const canUseAdReview = !!(
    adReviewEnabled &&
    (isAdmin || user?.permissions?.can_use_ad_review !== false)
  );

  return {
    // 公司層級開關
    faqEnabled: features.faq_enabled,
    riskLevelEnabled: features.risk_level_enabled,
    adReviewEnabled,
    aiRulesEnabled: features.ai_rules_enabled,
    aiRulesetUploadEnabled: features.ai_ruleset_upload_enabled,
    imageDescriptionEnabled: features.image_description_enabled,
    legaltechLibraryEnabled: features.legaltech_library_enabled,

    // 全文審閱存取
    canUseAdReview,

    // FAQ 編輯權限：Admin 或 can_edit_faq=true
    canEditFaq: !!(isAdmin || user?.permissions?.can_edit_faq),

    // AI 規則審閱：公司已開通 + 全文審閱可用 + (Admin 或 can_use_ai_rules=true)
    canUseAiRules: !!(
      adReviewEnabled &&
      features.ai_rules_enabled &&
      (isAdmin || user?.permissions?.can_use_ai_rules)
    ),

    // 上傳 AI 規則集：公司已開通 + 全文審閱可用 + (Admin 或 can_upload_rules=true)
    canUploadAiRuleset: !!(
      adReviewEnabled &&
      features.ai_ruleset_upload_enabled &&
      (isAdmin || user?.permissions?.can_upload_rules)
    ),

    // 驗測模式：per-user；canToggleValidationMode 控制是否顯示 Switch
    validationMode,
    canToggleValidationMode,

    // AI 規則優化：公司已開通 + 全文審閱可用 + (Admin 或 can_edit_review_cards=true)
    aiRuleOptimizationEnabled: features.ai_rule_optimization_enabled,
    canEditReviewCards: !!(
      adReviewEnabled &&
      features.ai_rule_optimization_enabled &&
      (isAdmin || user?.permissions?.can_edit_review_cards)
    ),

    // 合約比對：公司已開通 + (Admin 或 can_use_contract_comparison=true)
    contractComparisonEnabled: features.contract_comparison_enabled,
    canUseContractComparison: !!(
      features.contract_comparison_enabled &&
      (isAdmin || user?.permissions?.can_use_contract_comparison)
    ),
  };
};
